import React, { useEffect, useState } from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { cookieService, rentChaserService, userService } from '../../_services';
import { STRING_CONSTANTS } from '../../utils/constants/stringConstants';
import { Data } from '../../_services/collections';
import ToastHeader from '../../components/toastHeader';
import './AppSelector.css';

type Product = {
  key: string;
  name: string;
  desc: string;
  route: string;
  external?: boolean;
  sso?: boolean;
  // Backend product slug for the generic enable-product endpoint.
  enableSlug?: string;
  // Special in-app provisioning (e.g. RentChaser) — handled by component.
  enableInApp?: 'rentchaser';
  // Products that are joined via their own signup page rather than an enable call.
  enableRoute?: string;
  enableLabel?: string;
};

const PRODUCTS: Product[] = [
  { key: 'isAccountsSignup', name: 'Invoice Chaser', desc: 'AI-powered accounts receivable management', route: '/ar', enableSlug: 'invoice_chaser' },
  { key: 'isInterviewSimulatorSignup', name: 'Interview Simulator', desc: 'AI interview practice & hiring tool', route: '/interview-simulator/dashboard', enableSlug: 'interview_simulator' },
  { key: 'isLegalAssistSignup', name: 'Legal Assist', desc: 'AI legal document assistant', route: 'https://simulation.mploi.com/legal', external: true, enableSlug: 'legal_assist' },
  { key: 'isHrSimulatorSignup', name: 'HR Simulator', desc: 'AI HR training & scenarios', route: 'https://simulation.mploi.com/hr', external: true, enableSlug: 'hr_simulator' },
  { key: 'isRentChaserSignup', name: 'RentChaser', desc: 'AI-powered rent collection', route: 'https://rentchaser.mploi.com', external: true, sso: true, enableInApp: 'rentchaser' },
  { key: 'isArAffiliateSignup', name: 'Affiliate Dashboard', desc: 'Refer Invoice Chaser customers, track referrals and commission', route: '/ar/affiliate/dashboard', enableRoute: '/accounts/affiliate', enableLabel: 'Join' },
];

interface AppSelectorProps extends RouteComponentProps<any> {}

const launchRentChaserSso = async () => {
  const res: any = await rentChaserService.getLoginLink();
  const url = res?.data?.url || res?.url;
  if (!url) throw new Error('No SSO URL returned');
  window.location.href = url;
};

const refreshUserCookie = async () => {
  try {
    const fresh: any = await userService.getUserData();
    if (fresh) {
      cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.user, fresh);
      return fresh?.user || fresh;
    }
  } catch (_) { /* non-fatal */ }
  return null;
};

const AppSelector: React.FC<AppSelectorProps> = (props) => {
  const [user, setUser] = useState<any>(null);
  const [firstName, setFirstName] = useState('');
  const [enabling, setEnabling] = useState<string | null>(null);

  useEffect(() => {
    const userData = cookieService.getCookie(STRING_CONSTANTS.cookieStrings.user) as Data;
    const u = userData?.user;
    if (!u) {
      props.history.push('/signin');
      return;
    }
    setUser(u);
    setFirstName(u.firstName || '');
  }, []);

  const handleLogout = () => {
    cookieService.removeAllCookies();
    props.history.push('/signin');
  };

  const isSubscribed = (product: Product) => !!(user && (user as any)[product.key]);

  const handleLaunch = async (product: Product) => {
    try {
      if (product.sso && product.key === 'isRentChaserSignup') {
        await launchRentChaserSso();
        return;
      }
      if (product.external) {
        window.location.href = product.route;
      } else {
        props.history.push(product.route);
      }
    } catch (e) {
      ToastHeader(e);
    }
  };

  const handleEnable = async (product: Product) => {
    if (enabling) return;
    setEnabling(product.key);
    try {
      if (product.enableRoute) {
        props.history.push(product.enableRoute);
        return;
      }
      if (product.enableInApp === 'rentchaser') {
        await rentChaserService.signup();
        await refreshUserCookie();
        await launchRentChaserSso();
        return;
      }
      if (product.enableSlug) {
        await userService.enableProduct(product.enableSlug);
        const freshUser = await refreshUserCookie();
        if (freshUser) setUser(freshUser);
        props.history.push(product.route);
        return;
      }
    } catch (e) {
      ToastHeader(e);
    } finally {
      setEnabling(null);
    }
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      fontFamily: "'DM Sans', sans-serif",
      backgroundColor: '#F8F9FA',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
    },
    title: {
      fontFamily: "'Urbanist', sans-serif",
      fontWeight: 700,
      fontSize: '28px',
      color: '#141414',
      marginBottom: '8px',
      textAlign: 'center',
    },
    subtitle: {
      fontSize: '16px',
      color: '#555555',
      marginBottom: '40px',
      textAlign: 'center',
    },
    footer: {
      marginTop: '40px',
      fontSize: '14px',
      color: '#888',
      textAlign: 'center',
    },
    logoutLink: {
      color: '#0065FF',
      cursor: 'pointer',
      textDecoration: 'underline',
      background: 'none',
      border: 'none',
      fontSize: '14px',
    },
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Choose Your App - MPLOI</title>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Urbanist:wght@400;500;700&display=swap" rel="stylesheet" />
      </Helmet>
      <div style={styles.container}>
        <h1 style={styles.title}>
          {firstName ? `Welcome back, ${firstName}!` : 'Welcome back!'}
        </h1>
        <p style={styles.subtitle}>Choose an app to launch — or enable another one</p>

        <div className="app-selector-grid">
          {PRODUCTS.map((product) => {
            const subscribed = isSubscribed(product);
            const isEnablingThis = enabling === product.key;
            return (
              <div
                key={product.key}
                className={`app-selector-card${subscribed ? '' : ' app-selector-card--locked'}`}
                onClick={() => (subscribed ? handleLaunch(product) : handleEnable(product))}
                style={!subscribed ? { opacity: 0.85, borderStyle: 'dashed' } : undefined}
              >
                <div className="card-name">{product.name}</div>
                <div className="card-desc">{product.desc}</div>
                <button className="card-btn" disabled={isEnablingThis}>
                  {subscribed
                    ? 'Launch'
                    : isEnablingThis
                      ? 'Enabling…'
                      : (product.enableLabel || 'Enable')}
                </button>
              </div>
            );
          })}
        </div>

        <div style={styles.footer}>
          <button style={styles.logoutLink} onClick={handleLogout}>
            Log out
          </button>
        </div>
      </div>
    </>
  );
};

export default withRouter(AppSelector);
